import React, { useState, useEffect, useCallback } from 'react';
import { formatDateLocale, formatDateTimeLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { useTreatXAdminAuth } from '../../context/TreatXAdminAuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/Toaster';
import {
  ArrowLeft, Shield, CheckCircle2, XCircle, Clock, Building2,
  FileText, Eye, Loader2, RefreshCw, AlertTriangle, Activity,
  TrendingUp, Users
} from 'lucide-react';
import api from '../../utils/api';
import { authHeaders, maybeClearStaleToken } from '../../utils/unifiedAuth';
import AdminFieldVerificationPanel from './AdminFieldVerificationPanel';


const API = process.env.REACT_APP_BACKEND_URL || '';

// OWNER-HYGIENE-1 (2026-05-21): authenticated blob download for verification
// documents.  Replaces the prior `window.open('/admin/verification-documents/'
// + id + '?auth=' + token)` token-in-URL pattern (anti-pattern: token in
// query string is logged, cached, and shared). Canonical fix: authenticated
// fetch + Blob + synthesised anchor click, matching the AdminProviderDomain
// pattern.  No token in URL, no public download endpoint.
async function authedDocumentOpen(documentId, fileName) {
  const url = `${API}/api/admin/documents/${documentId}/file`;
  try {
    const res = await fetch(url, { headers: authHeaders() });
    if (maybeClearStaleToken(res)) return;
    if (!res.ok) {
      toast({
        title: 'Document download failed',
        description: res.status === 401
          ? 'Session expired or unauthorized.'
          : res.status === 403
            ? 'Forbidden (entity.manage required).'
            : `HTTP ${res.status}.`,
        variant: 'destructive',
      });
      return;
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    if (fileName) a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch (e) {
    toast({
      title: 'Document download failed',
      description: e.message || 'Network error.',
      variant: 'destructive',
    });
  }
}

const VerificationBadge = ({ level }) => {
  const styles = {
    verified: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    pending_verification: 'bg-amber-100 text-amber-700 border-amber-200',
    unverified: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  return (
    <Badge className={`text-[10px] ${styles[level] || styles.unverified}`} data-testid={`badge-${level}`}>
      {level === 'verified' && <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />}
      {level === 'pending_verification' && <Clock className="h-2.5 w-2.5 mr-0.5" />}
      {level === 'unverified' && <XCircle className="h-2.5 w-2.5 mr-0.5" />}
      {level?.replace(/_/g, ' ')}
    </Badge>
  );
};

// ── Pending Verifications Tab ────────────────────────────────

const PendingTab = ({ pending, loading, onApprove, onReject, onSelectClinic }) => {
  if (loading) return <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin text-blue-500 mx-auto" /></div>;
  if (!pending?.length) return <div className="py-8 text-center text-sm text-slate-400" data-testid="no-pending">No pending verifications</div>;

  return (
    <div className="space-y-3" data-testid="pending-list">
      {pending.map(p => (
        <Card key={p.clinic_id} className="border border-amber-200" data-testid={`pending-card-${p.clinic_id}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-slate-400" />
                  {p.clinic_name}
                </h3>
                <p className="text-[11px] text-slate-500 mt-0.5">ID: {p.clinic_id}</p>
              </div>
              <VerificationBadge level={p.verification_level} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 text-xs">
              {p.years_of_experience != null && (
                <div className="bg-slate-50 rounded p-2">
                  <div className="text-slate-500">Experience</div>
                  <div className="font-medium text-slate-700">{p.years_of_experience} years</div>
                </div>
              )}
              {p.procedures_performed != null && (
                <div className="bg-slate-50 rounded p-2">
                  <div className="text-slate-500">Procedures</div>
                  <div className="font-medium text-slate-700">{p.procedures_performed?.toLocaleString()}</div>
                </div>
              )}
              {p.international_patient_volume != null && (
                <div className="bg-slate-50 rounded p-2">
                  <div className="text-slate-500">Intl Patients</div>
                  <div className="font-medium text-slate-700">{p.international_patient_volume?.toLocaleString()}/yr</div>
                </div>
              )}
              {p.doctor_experience_years != null && (
                <div className="bg-slate-50 rounded p-2">
                  <div className="text-slate-500">Doctor Exp</div>
                  <div className="font-medium text-slate-700">{p.doctor_experience_years} years</div>
                </div>
              )}
            </div>

            {p.accreditations?.length > 0 && (
              <div className="mb-3">
                <div className="text-[11px] text-slate-500 mb-1">Accreditations:</div>
                <div className="flex flex-wrap gap-1">
                  {p.accreditations.map((a, i) => (
                    <Badge key={i} variant="outline" className="text-[10px]">
                      {a.accreditation_type} {a.certificate_id && `(${a.certificate_id})`}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {p.pending_documents?.length > 0 && (
              <div className="mb-3">
                <div className="text-[11px] text-slate-500 mb-1">Documents ({p.pending_documents.length}):</div>
                <div className="flex flex-wrap gap-1.5">
                  {p.pending_documents.map(d => (
                    <button
                      key={d.document_id}
                      onClick={() => authedDocumentOpen(d.document_id, d.file_name)}
                      className="flex items-center gap-1 bg-blue-50 border border-blue-200 rounded px-2 py-0.5 text-[10px] text-blue-700 hover:bg-blue-100"
                      data-testid={`doc-${d.document_id}`}
                    >
                      <FileText className="h-2.5 w-2.5" />
                      {d.file_name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2 border-t border-slate-100">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7"
                onClick={() => onApprove(p.clinic_id)} data-testid={`approve-${p.clinic_id}`}>
                <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 text-xs h-7"
                onClick={() => onReject(p.clinic_id)} data-testid={`reject-${p.clinic_id}`}>
                <XCircle className="h-3 w-3 mr-1" /> Reject
              </Button>
              <Button size="sm" variant="ghost" className="text-xs h-7 ml-auto"
                onClick={() => onSelectClinic(p.clinic_id)} data-testid={`view-detail-${p.clinic_id}`}>
                <Eye className="h-3 w-3 mr-1" /> Full Details
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ── Clinic Detail Panel ──────────────────────────────────────

const ClinicDetailPanel = ({ clinicId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [outcomeForm, setOutcomeForm] = useState({ success_rate: '', complication_rate: '', revision_rate: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!clinicId) return;
    setLoading(true);
    api.get(`/admin/clinic-trust/${clinicId}`, {
    })
      .then(({ data }) => data)
      .then(d => {
        setData(d);
        if (d.outcome_metrics) {
          setOutcomeForm({
            success_rate: d.outcome_metrics.success_rate ?? '',
            complication_rate: d.outcome_metrics.complication_rate ?? '',
            revision_rate: d.outcome_metrics.revision_rate ?? '',
          });
        }
      })
      .finally(() => setLoading(false));
  }, [clinicId]);

  const saveOutcomeMetrics = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/admin/clinic-trust/${clinicId}/outcome-metrics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json'},
        body: JSON.stringify({
          success_rate: parseFloat(outcomeForm.success_rate) || null,
          complication_rate: parseFloat(outcomeForm.complication_rate) || null,
          revision_rate: parseFloat(outcomeForm.revision_rate) || null,
          source: 'admin_input',
        }),
      });
      if (r.ok) toast({ title: 'Saved', description: 'Outcome metrics updated (internal only)' });
    } catch {
      toast({ title: 'Error', description: 'Failed to save', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-blue-500" /></div>;

  const t = data?.trust_metrics;
  const p = data?.platform_metrics;
  const o = data?.outcome_metrics;

  return (
    <div className="space-y-4" data-testid="clinic-detail-panel">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-from-detail"><ArrowLeft className="h-4 w-4" /></Button>
        <h3 className="text-sm font-semibold text-slate-800">Clinic: {clinicId}</h3>
        {t && <VerificationBadge level={t.verification_level} />}
      </div>

      {/* Trust Metrics */}
      {t && (
        <Card className="border border-slate-200" data-testid="trust-metrics-card">
          <CardHeader className="py-2 px-4"><CardTitle className="text-xs font-medium text-slate-600">Submitted Trust Data</CardTitle></CardHeader>
          <CardContent className="px-4 pb-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <MetricCell label="Experience" value={t.years_of_experience ? `${t.years_of_experience} yrs` : '-'} />
              <MetricCell label="Procedures" value={t.procedures_performed?.toLocaleString() || '-'} />
              <MetricCell label="Last 12mo" value={t.procedures_performed_last_12_months?.toLocaleString() || '-'} />
              <MetricCell label="Intl Patients" value={t.international_patient_volume?.toLocaleString() || '-'} />
              <MetricCell label="Doctor Exp" value={t.doctor_experience_years ? `${t.doctor_experience_years} yrs` : '-'} />
              <MetricCell label="Source" value={t.verification_source || '-'} />
              <MetricCell label="Verified By" value={t.verified_by_admin_id || '-'} />
              <MetricCell label="Verified At" value={t.verified_at ? formatDateLocale(t.verified_at) : '-'} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Platform Metrics */}
      {p && (
        <Card className="border border-blue-200" data-testid="platform-metrics-card">
          <CardHeader className="py-2 px-4"><CardTitle className="text-xs font-medium text-blue-600 flex items-center gap-1"><Activity className="h-3 w-3" /> Platform Performance</CardTitle></CardHeader>
          <CardContent className="px-4 pb-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <MetricCell label="Avg Response" value={p.response_speed_hours != null ? `${p.response_speed_hours}h` : '-'} />
              <MetricCell label="Acceptance" value={`${p.offer_acceptance_rate || 0}%`} />
              <MetricCell label="Payment" value={`${p.payment_completion_rate || 0}%`} />
              <MetricCell label="Complaints" value={p.complaint_rate || 0} />
              <MetricCell label="Refunds" value={p.refund_requested_count || 0} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Outcome Metrics (INTERNAL ONLY) */}
      <Card className="border border-red-200 bg-red-50/30" data-testid="outcome-metrics-card">
        <CardHeader className="py-2 px-4">
          <CardTitle className="text-xs font-medium text-red-600 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> Internal Outcome Metrics
            <Badge className="ml-2 bg-red-100 text-red-600 border-red-200 text-[9px]">ADMIN ONLY</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <label className="text-[10px] text-slate-500">Success Rate (%)</label>
              <input type="number" step="0.1" min="0" max="100"
                className="w-full border rounded px-2 py-1 text-xs mt-0.5"
                value={outcomeForm.success_rate}
                onChange={e => setOutcomeForm(p => ({ ...p, success_rate: e.target.value }))}
                data-testid="outcome-success-rate"
              />
            </div>
            <div>
              <label className="text-[10px] text-slate-500">Complication Rate (%)</label>
              <input type="number" step="0.1" min="0" max="100"
                className="w-full border rounded px-2 py-1 text-xs mt-0.5"
                value={outcomeForm.complication_rate}
                onChange={e => setOutcomeForm(p => ({ ...p, complication_rate: e.target.value }))}
                data-testid="outcome-complication-rate"
              />
            </div>
            <div>
              <label className="text-[10px] text-slate-500">Revision Rate (%)</label>
              <input type="number" step="0.1" min="0" max="100"
                className="w-full border rounded px-2 py-1 text-xs mt-0.5"
                value={outcomeForm.revision_rate}
                onChange={e => setOutcomeForm(p => ({ ...p, revision_rate: e.target.value }))}
                data-testid="outcome-revision-rate"
              />
            </div>
          </div>
          <Button size="sm" className="text-xs h-7 bg-red-600 hover:bg-red-700 text-white"
            onClick={saveOutcomeMetrics} disabled={saving} data-testid="save-outcome-btn">
            {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Save Outcome Metrics
          </Button>
          <p className="text-[9px] text-red-400 mt-1">These metrics are strictly internal and never exposed to clinics or patients.</p>
        </CardContent>
      </Card>

      {/* Audit Log */}
      {data?.audit_log?.length > 0 && (
        <Card className="border border-slate-200" data-testid="audit-log-card">
          <CardHeader className="py-2 px-4"><CardTitle className="text-xs font-medium text-slate-600">Audit Trail</CardTitle></CardHeader>
          <CardContent className="px-4 pb-3">
            <div className="space-y-1.5">
              {data.audit_log.map((a, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px] text-slate-600">
                  <Badge variant="outline" className="text-[9px] shrink-0">{a.action}</Badge>
                  <span className="text-slate-400">{formatDateTimeLocale(a.timestamp)}</span>
                  <span className="text-slate-500 truncate">{a.notes}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* PRD1.7 Step 4B: Per-Field Verification Panel */}
      <AdminFieldVerificationPanel clinicId={clinicId} />
    </div>
  );
};

const MetricCell = ({ label, value }) => (
  <div className="bg-white rounded border border-slate-100 p-1.5">
    <div className="text-[10px] text-slate-400">{label}</div>
    <div className="text-xs font-medium text-slate-700">{value}</div>
  </div>
);

// ═══════════════════════════════════════════════════════════════
//  MAIN PAGE
// ═══════════════════════════════════════════════════════════════

export default function ClinicVerificationCenter() {
  const { admin } = useTreatXAdminAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchPending = useCallback(async () => {
    try {
      setLoading(true);
      // TREATX-ACCRED-ADMIN-QUEUE-01 — `api` is an axios instance, so the
      // response has no `.ok` (that is `fetch`). The old `if (r.ok)` was never
      // true, so this queue rendered "Pending (0)" even while the endpoint
      // returned pending clinics — an uploaded accreditation certificate could
      // never be reached for verification from here. Axios already rejects on
      // a non-2xx status, which the catch below handles.
      const r = await api.get(`/admin/clinic-trust/pending`);
      setPending(r.data?.pending || []);
    } catch (err) {
      console.error('Fetch pending:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (admin) fetchPending(); }, [admin, fetchPending]);

  const handleVerify = async (clinicId, action) => {
    try {
      const r = await api.post(`/admin/clinic-trust/${clinicId}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json'},
        body: JSON.stringify({ action, notes: `Admin ${action}d via Verification Center` }),
      });
      if (r.ok) {
        toast({ title: action === 'approve' ? 'Approved' : 'Rejected', description: `Clinic ${clinicId} ${action}d` });
        fetchPending();
      }
    } catch {
      toast({ title: 'Error', description: 'Verification action failed', variant: 'destructive' });
    }
  };

  const handleRefreshPlatformMetrics = async () => {
    setRefreshing(true);
    try {
      const r = await api.post(`/admin/clinic-platform-metrics/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json'},
      });
      if (r.ok) {
        const d = r.data;
        toast({ title: 'Refreshed', description: `Platform metrics updated for ${d.clinics_updated} clinics` });
      }
    } catch {
      toast({ title: 'Error', description: 'Refresh failed', variant: 'destructive' });
    } finally {
      setRefreshing(false);
    }
  };

  if (!admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Card className="w-96"><CardContent className="p-6 text-center">
          <Shield className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500 mb-3">Admin login required</p>
          <Button onClick={() => navigate('/login')} data-testid="login-redirect">Sign In</Button>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="clinic-verification-center">
      <Toaster />

      <header className="bg-[#122033] text-white border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10"
              onClick={() => navigate('/treatx-admin/analytics')} data-testid="back-to-analytics">
              <ArrowLeft className="h-4 w-4 mr-1" /> Analytics
            </Button>
            <div className="h-5 w-px bg-white/20" />
            <h1 className="text-lg font-semibold tracking-tight font-[Manrope] flex items-center gap-2">
              <Shield className="h-4 w-4" /> Clinic Verification Center
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10"
              onClick={handleRefreshPlatformMetrics} disabled={refreshing} data-testid="refresh-platform-metrics">
              <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh Platform Metrics
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {selectedClinic ? (
          <ClinicDetailPanel clinicId={selectedClinic} onBack={() => setSelectedClinic(null)} />
        ) : (
          <>
          <AccreditationDataCheck />
          <Tabs defaultValue="pending">
            <TabsList className="mb-4" data-testid="verification-tabs">
              <TabsTrigger value="pending" className="text-xs" data-testid="tab-pending">
                Pending ({pending.length})
              </TabsTrigger>
              <TabsTrigger value="all" className="text-xs" data-testid="tab-all">
                All Clinics
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending">
              <PendingTab
                pending={pending}
                loading={loading}
                onApprove={(id) => handleVerify(id, 'approve')}
                onReject={(id) => handleVerify(id, 'reject')}
                onSelectClinic={setSelectedClinic}
              />
            </TabsContent>

            <TabsContent value="all">
              <AllClinicsTab onSelectClinic={setSelectedClinic} />
            </TabsContent>
          </Tabs>
          </>
        )}
      </main>
    </div>
  );
}

// ── Accreditation Data Check (Super Admin master-data integrity) ─────────────
// TREATX-ACCRED-MASTER-MAINT-01. Business-readable CHECK (read-only) + FIX
// (consolidate duplicates + enforce unique slug). Owner needs no DB knowledge.
function AccreditationDataCheck() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [report, setReport] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [fixResult, setFixResult] = useState(null);

  const runCheck = async () => {
    setLoading(true); setFixResult(null);
    try {
      const { data } = await api.get('/admin/accreditations/integrity-check', { headers: authHeaders() });
      setReport(data);
    } catch (e) {
      toast({ title: t('accreditationMaintenance.errorTitle'), description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const runFix = async () => {
    setFixing(true); setConfirming(false);
    try {
      const { data } = await api.post('/admin/accreditations/fix-duplicates', {}, { headers: authHeaders() });
      setFixResult(data);
      setReport(data.after ? { success: true, ...data.after } : report);
      toast({
        title: t('accreditationMaintenance.title'),
        description: (data.before?.duplicate_group_count ? t('accreditationMaintenance.fixedToast') : t('accreditationMaintenance.alreadyClean')),
      });
    } catch (e) {
      toast({ title: t('accreditationMaintenance.errorTitle'), description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setFixing(false); }
  };

  const dupCount = report?.duplicate_group_count || 0;
  const protectionActive = !!report?.unique_slug_protection_active;

  return (
    <Card className="mb-4 border-blue-200" data-testid="accreditation-data-check">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Shield className="h-4 w-4 text-blue-600" />
          {t('accreditationMaintenance.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={runCheck} disabled={loading} data-testid="accreditation-check-btn">
            {loading
              ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />{t('accreditationMaintenance.checking')}</>
              : <><RefreshCw className="h-3 w-3 mr-1" />{t('accreditationMaintenance.checkBtn')}</>}
          </Button>
          {report && dupCount > 0 && !confirming && (
            <Button size="sm" variant="destructive" onClick={() => setConfirming(true)} disabled={fixing} data-testid="accreditation-fix-btn">
              {t('accreditationMaintenance.fixBtn')}
            </Button>
          )}
        </div>

        {confirming && (
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm" data-testid="accreditation-fix-confirm">
            <p className="mb-2 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" />{t('accreditationMaintenance.confirm')}</p>
            <div className="flex gap-2">
              <Button size="sm" variant="destructive" onClick={runFix} disabled={fixing} data-testid="accreditation-fix-confirm-btn">
                {fixing ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />{t('accreditationMaintenance.fixing')}</> : t('accreditationMaintenance.fixBtn')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {report && (
          <div className="text-sm space-y-1" data-testid="accreditation-check-result">
            {dupCount === 0 ? (
              <>
                <p className="flex items-center gap-2 text-emerald-700 font-medium">
                  <CheckCircle2 className="h-4 w-4" />{t('accreditationMaintenance.cleanTitle')}
                </p>
                <p className="text-gray-600">{t('accreditationMaintenance.cleanLine', { total: report.total_records, unique: report.unique_accreditations })}</p>
                <p className="text-gray-600">{t('accreditationMaintenance.noDuplicates')}</p>
              </>
            ) : (
              <>
                <p className="text-gray-700">{t('accreditationMaintenance.totalRecords')}: <b>{report.total_records}</b></p>
                <p className="text-gray-700">{t('accreditationMaintenance.uniqueAccreditations')}: <b>{report.unique_accreditations}</b></p>
                <p className="text-gray-700">{t('accreditationMaintenance.duplicateGroups')}: <b>{dupCount}</b></p>
                <ul className="ml-4 list-disc text-gray-600">
                  {report.duplicate_groups?.map((g) => (
                    <li key={g.slug} data-testid={`dup-group-${g.slug}`}>{g.label} — {g.count} {t('accreditationMaintenance.recordsWord')}</li>
                  ))}
                </ul>
              </>
            )}
            <p className={protectionActive ? 'text-emerald-700' : 'text-amber-700'}>
              {t('accreditationMaintenance.uniqueProtection')}: <b>{protectionActive ? t('accreditationMaintenance.active') : t('accreditationMaintenance.notActive')}</b>
            </p>
          </div>
        )}

        {fixResult && (
          <div className="text-xs bg-slate-50 border rounded-lg p-3 space-y-1" data-testid="accreditation-fix-result">
            <p className="font-medium text-gray-700">{t('accreditationMaintenance.beforeLabel')}: {fixResult.before?.total_records} / {fixResult.before?.duplicate_group_count} dup</p>
            <p className="font-medium text-gray-700">{t('accreditationMaintenance.afterLabel')}: {fixResult.after?.total_records} / {fixResult.after?.duplicate_group_count} dup</p>
            {fixResult.success && <p className="text-emerald-700 font-semibold">{t('accreditationMaintenance.resultPass')}</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── All Clinics Tab ──────────────────────────────────────────

function AllClinicsTab({ onSelectClinic }) {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/clinics`, {
    })
      .then(({ data }) => data).catch(() => ({ clinics: [] }))
      .then(d => setClinics(d.clinics || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-blue-500" /></div>;

  return (
    <div className="space-y-2" data-testid="all-clinics-list">
      {clinics.map(c => (
        <div key={c.clinic_id || c.id} className="flex items-center justify-between bg-white border rounded-lg p-3 hover:bg-slate-50 cursor-pointer"
          onClick={() => onSelectClinic(c.clinic_id || c.id)} data-testid={`clinic-row-${c.clinic_id || c.id}`}>
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-slate-400" />
            <span className="text-sm font-medium text-slate-700">{c.clinic_name || c.business_name || c.clinic_id}</span>
          </div>
          <Eye className="h-4 w-4 text-slate-400" />
        </div>
      ))}
      {!clinics.length && <p className="text-center text-sm text-slate-400 py-6">No clinics found</p>}
    </div>
  );
}
